import React, { useState, useEffect, useRef } from "react";
import { Box, Paper, Stack } from "@mui/material/";
import {
  selectSessionGroups,
  reloadDataPerSearchId,
  selectChatSessionId,
  setChatSessionId,
} from "../redux/dataSlice";
import { selectUser } from "../redux/identitySlice";
import { useSelector, useDispatch } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const ROW_HEIGHT = 44;

const Item = styled(Paper)(() => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  padding: "0 12px",
  height: `${ROW_HEIGHT}px`,
  lineHeight: `${ROW_HEIGHT}px`,
  display: "block",
  textAlign: "left",
  cursor: "pointer",
  borderRadius: "8px",
  fontSize: "15px",
  color: "#444",
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
  transition: "background-color 0.15s ease",
  "&.MuiPaper-root:hover": {
    backgroundColor: "#869da630",
    boxShadow: "none",
  },
}));

function HistorySelector({ leftPanelVisible, hasMore, loadingMore, onLoadMore }) {
  const sessionGroups = useSelector(selectSessionGroups);
  const user = useSelector(selectUser);
  const chatSessionId = useSelector(selectChatSessionId);
  const [currentItem, setCurrentItem] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState({});
  // Track which search_ids have already been animated — never re-animate
  const animatedIds = useRef(new Set());
  const dispatch = useDispatch();

  const filteredGroups = sessionGroups.filter(g =>
    !user ? g.session_id === chatSessionId : true
  ).map(g => ({
    ...g,
    // newest search first within each group
    searches: [...g.searches].filter(s => s.search_summary).reverse(),
  })).filter(g => g.searches.length > 0);

  useEffect(() => {
    if (!chatSessionId) { setCurrentItem(null); }
  }, [chatSessionId]);

  // Auto-expand the session that matches the current chat
  useEffect(() => {
    if (!chatSessionId) return;
    const group = filteredGroups.find(g => g.session_id === chatSessionId);
    if (group && group.searches.length > 1) {
      setExpandedSessions(prev => ({ ...prev, [group.session_id]: true }));
    }
  }, [chatSessionId, sessionGroups]);

  const selectSearch = (searchId, sessionId) => {
    dispatch(reloadDataPerSearchId(searchId));
    dispatch(setChatSessionId(sessionId));
  };

  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => ({ ...prev, [sessionId]: !prev[sessionId] }));
  };

  const label = (s) =>
    s?.search_summary?.charAt(0).toUpperCase() + s?.search_summary?.slice(1);

  // Only animate items that haven't been seen before — all others render immediately
  const getFadeClass = (searchId) => {
    if (animatedIds.current.has(searchId)) return undefined;
    animatedIds.current.add(searchId);
    return "fade-in-item";
  };

  // Collapsed: numbered circles for every search
  if (!leftPanelVisible) {
    const allItems = filteredGroups.flatMap(g => g.searches);
    return (
      <Stack sx={{ pt: 1, alignItems: "center" }}>
        {allItems.map((item, idx) => (
          <Tooltip key={item.search_id} title={label(item)} placement="right">
            <Box
              className={getFadeClass(item.search_id)}
              sx={{
                height: `${ROW_HEIGHT}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Item
                onClick={() => { selectSearch(item.search_id, item.session_id); setCurrentItem(item); }}
                sx={{
                  width: "calc(100% - 16px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: 500,
                  backgroundColor: currentItem === item ? "#869da6" : "#f0f0f0",
                  color: currentItem === item ? "white" : "#666",
                  "&.MuiPaper-root:hover": {
                    backgroundColor: currentItem === item ? "#869da6" : "#869da699",
                    color: "white",
                    boxShadow: "none",
                  },
                }}
              >
                {idx + 1}
              </Item>
            </Box>
          </Tooltip>
        ))}
      </Stack>
    );
  }

  return (
    <Stack sx={{ pt: 1, px: 1, pb: hasMore ? "100px" : "100px" }}>
      {filteredGroups.map((group) => {
        const isMulti = group.searches.length > 1;
        const isOpen = !!expandedSessions[group.session_id];
        // newest is first in the array (reversed in filteredGroups above)
        const newestSearch = group.searches[0];

        if (!isMulti) {
          const item = newestSearch;
          return (
            <Item
              key={item.search_id}
              className={getFadeClass(item.search_id)}
              onClick={() => { selectSearch(item.search_id, item.session_id); setCurrentItem(item); }}
              sx={{
                backgroundColor: currentItem === item ? "#869da6" : "transparent",
                color: currentItem === item ? "white" : "#444",
                "&.MuiPaper-root:hover": {
                  backgroundColor: currentItem === item ? "#869da6" : "#869da630",
                  color: currentItem === item ? "white" : "#222",
                  boxShadow: "none",
                },
              }}
            >
              {label(item)}
            </Item>
          );
        }

        // Multi-search session — group header looks identical to a flat Item
        // but has a chevron on the right and clicks to expand/collapse
        return (
          <Box key={group.session_id} className={getFadeClass(newestSearch.search_id)}>
            <Item
              onClick={() => toggleSession(group.session_id)}
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#444",
                "&.MuiPaper-root:hover": {
                  backgroundColor: "#869da630",
                  color: "#222",
                  boxShadow: "none",
                },
              }}
            >
              <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label(newestSearch)}
              </Box>
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 16,
                  color: "#999",
                  flexShrink: 0,
                  ml: 0.5,
                  transition: "transform 0.2s ease",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Item>

            {isOpen && group.searches.map((item) => (
              <Item
                key={item.search_id}
                className={getFadeClass(item.search_id)}
                onClick={() => { selectSearch(item.search_id, item.session_id); setCurrentItem(item); }}
                sx={{
                  pl: "22px",
                  fontSize: "14px",
                  color: currentItem === item ? "white" : "#666",
                  backgroundColor: currentItem === item ? "#869da6" : "transparent",
                  "&.MuiPaper-root:hover": {
                    backgroundColor: currentItem === item ? "#869da6" : "#869da630",
                    color: currentItem === item ? "white" : "#222",
                    boxShadow: "none",
                  },
                }}
              >
                {label(item)}
              </Item>
            ))}
          </Box>
        );
      })}

      {hasMore && leftPanelVisible && (
        <Box
          onClick={!loadingMore ? onLoadMore : undefined}
          sx={{
            height: `${ROW_HEIGHT}px`,
            display: "flex",
            alignItems: "center",
            px: "12px",
            cursor: loadingMore ? "default" : "pointer",
            borderRadius: "8px",
            fontSize: "13px",
            color: loadingMore ? "#ccc" : "#aaa",
            transition: "color 0.15s ease",
            "&:hover": { color: loadingMore ? "#ccc" : "#555" },
          }}
        >
          {loadingMore ? "Loading…" : "Load more…"}
        </Box>
      )}

    </Stack>
  );
}

export { HistorySelector };
